import { Controller } from "@hotwired/stimulus"

// Toggle between "new prospect" and "existing account" modes on the opportunity
// form. In existing mode, debounced search hits the server and lets the user
// pick an Account; the chosen account_id is written to the hidden field, and
// the business name field is prefilled from the account.
export default class extends Controller {
  static targets = [
    "searchPanel", "query", "status", "results",
    "accountId", "selectedSummary", "selectedName"
  ]
  static values = {
    searchUrl: String,
    debounceMs: { type: Number, default: 300 }
  }

  switchMode(event) {
    const mode = event.target.value
    if (mode === "existing") {
      this.searchPanelTarget.classList.remove("d-none")
    } else {
      this.searchPanelTarget.classList.add("d-none")
      this.clear()
    }
  }

  // Checkbox variant (e.g. proposal form): show the search panel when checked,
  // hide and clear the selection when unchecked.
  toggleExisting(event) {
    if (event.target.checked) {
      this.searchPanelTarget.classList.remove("d-none")
    } else {
      this.searchPanelTarget.classList.add("d-none")
      this.clear()
    }
  }

  scheduleSearch() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.search(), this.debounceMsValue)
  }

  preventSubmit(event) {
    if (event.target.closest("textarea, button, [type='submit']")) return
    event.preventDefault()
  }

  async search() {
    const q = this.queryTarget.value.trim()
    if (q.length < 2) {
      this.clearResults()
      this.statusTarget.textContent = "Type at least 2 characters"
      return
    }

    this.statusTarget.textContent = "Searching..."
    try {
      const url = `${this.searchUrlValue}?q=${encodeURIComponent(q)}`
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      const data = await response.json()
      this.renderResults(data.accounts || [])
    } catch (err) {
      this.statusTarget.textContent = `Search failed: ${err.message}`
    }
  }

  renderResults(accounts) {
    if (accounts.length === 0) {
      this.clearResults()
      this.statusTarget.textContent = "No matches"
      return
    }
    this.statusTarget.textContent = `${accounts.length} result(s)`
    this.resultsTarget.classList.remove("d-none")

    this.resultsTarget.innerHTML = accounts.map(a => `
      <button type="button" class="list-group-item list-group-item-action"
              data-action="click->sales-account-picker#select"
              data-account='${this.esc(JSON.stringify(a))}'>
        <div class="fw-semibold">${this.esc(a.name || '(no name)')}</div>
        <div class="text-muted small">
          ${a.dba ? `DBA: ${this.esc(a.dba)} . ` : ''}
          ${a.industry ? this.esc(a.industry) : ''}
          ${a.location ? ` . ${this.esc(a.location)}` : ''}
        </div>
      </button>
    `).join("")
  }

  select(event) {
    const btn = event.currentTarget
    const a   = JSON.parse(btn.dataset.account.replace(/&quot;/g, '"').replace(/&#39;/g, "'"))

    this.accountIdTarget.value = a.id
    this.selectedNameTarget.textContent = a.name
    this.selectedSummaryTarget.classList.remove("d-none")
    this.selectedSummaryTarget.classList.add("d-flex")
    if (this.hasSearchPanelTarget) this.searchPanelTarget.classList.add("d-none")
    this.clearResults()
    this.queryTarget.value = ""
    this.statusTarget.textContent = ""

    // Prefill any empty field whose corresponding account data is present.
    this.fill('sales_opportunity[business_name]',              a.name)
    this.fill('sales_opportunity[point_of_contact]',           a.contact_name)
    this.fill('sales_opportunity[email]',                      a.contact_email)
    this.fill('sales_opportunity[phone_number]',               a.contact_phone)
    this.fillSelect('sales_opportunity[industry]',             a.industry)
    this.fillSelect('sales_opportunity[entity_structure]',     a.legal_structure)
    this.fillSelect('sales_opportunity[current_accounting_system]', a.accounting_system)
    this.fill('sales_opportunity[current_payroll_provider]',   a.payroll_provider)

    // Discovery answers: cash/accrual
    this.fillSelect('sales_opportunity[discovery_answers][cash_or_accrual]', a.book_basis)

    this.dispatch("selected", { detail: { account: a } })
  }

  fill(name, value) {
    if (!value) return
    const el = document.querySelector(`input[name="${name}"], textarea[name="${name}"]`)
    if (el && !el.value) el.value = value
  }

  fillSelect(name, value) {
    if (!value) return
    const el = document.querySelector(`select[name="${name}"], select[name="${name}[]"]`)
    if (!el) return
    const tokens = String(value).split(";").map(token => token.trim()).filter(Boolean)
    const matched = tokens
      .map(token => Array.from(el.options).find(option => option.value.toLowerCase() === token.toLowerCase())?.value)
      .filter(Boolean)
    if (!matched.length) return
    if (el.multiple) {
      if (el.tomselect) {
        if (el.tomselect.getValue().length) return
        el.tomselect.setValue(matched)
      } else if (!Array.from(el.selectedOptions).length) {
        Array.from(el.options).forEach(option => { option.selected = matched.includes(option.value) })
      }
      return
    }
    if (el.value) return
    el.value = matched[0]
  }

  clear() {
    if (this.hasAccountIdTarget) this.accountIdTarget.value = ""
    if (this.hasSelectedSummaryTarget) {
      this.selectedSummaryTarget.classList.add("d-none")
      this.selectedSummaryTarget.classList.remove("d-flex")
    }
    if (this.hasSearchPanelTarget) this.searchPanelTarget.classList.remove("d-none")
    this.clearResults()
    if (this.hasQueryTarget) this.queryTarget.value = ""
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
    this.dispatch("cleared")
  }

  clearResults() {
    if (!this.hasResultsTarget) return
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("d-none")
  }

  esc(str) {
    return String(str).replace(/[&<>"']/g, c => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
